/* System defined core library */
import { HttpClient, HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError, finalize } from 'rxjs/operators';

/* User defined library/components */
import { LoaderService } from '../shared/services/loader.service';
import { BootstrapParams } from '../../../angular-src/bootstrap-params';
import { genToken } from './generate-token.js';

@Injectable()
export class HttpService {
  constructor(private http: HttpClient, private loaderService: LoaderService, private bootstrapParams: BootstrapParams,) { }

  get(url: string, token?: string, properties?: Array<string>, orderBy?: string, skip?: number, take?: number, sendAkamaiHeader: boolean = false, azureFunctionKey?: string): Observable<any> {
    return this.getting(true, url, sendAkamaiHeader, token, properties, orderBy, skip, take, azureFunctionKey);
  }

  getWithoutLoader(url: string, token?: string, properties?: Array<string>, orderBy?: string, skip?: number, take?: number, sendAkamaiHeader: boolean = false, azureFunctionKey?: string): Observable<any> {
    return this.getting(false, url, sendAkamaiHeader, token, properties, orderBy, skip, take, azureFunctionKey);
  }

  private getting(loader: boolean, url: string, sendAkamaiHeader: boolean, token?: string, properties?: Array<string>, orderBy?: string, skip?: number, take?: number, azureFunctionKey?: string): Observable<any> {
    if (loader) {
      this.loaderService.runningServicesCount++;
      if (this.loaderService.runningServicesCount >= 1) {
        this.showLoader();
      }
    }


    let queryParams: HttpParams = new HttpParams();
    if (properties) {
      queryParams.set('properties', properties.join(','));
    }
    if (orderBy) {
      queryParams.set('orderBy', orderBy);
    }
    if (skip) {
      queryParams.set('skip', skip.toString());
    }
    if (take) {
      queryParams.set('take', take.toString());
    }
    let headers = new HttpHeaders();
    headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'SessionId': this.bootstrapParams.sessionGuid
    });
    if (token) {
      if (!!azureFunctionKey) {
        headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
          'SessionId': this.bootstrapParams.sessionGuid,
          'X-Functions-Key': azureFunctionKey
        });
      } else {
        headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }
    } else if (token == "") {
      if (!!azureFunctionKey) {
        headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'X-Request-Id': genToken() as string,
          'SessionId': this.bootstrapParams.sessionGuid,
          'X-Functions-Key': azureFunctionKey
        });
      } else {
        headers = new HttpHeaders({
          'Content-Type': 'application/json',
          'X-Request-Id': genToken() as string,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }
    }
    let options: HttpRequest<any> = new HttpRequest<any>('GET', null, queryParams);

    if (sendAkamaiHeader) {
      headers = this.AddAkamaiBotDetectionHeader(headers);
    }

    return this.http.get(url, {
      params: queryParams,
      headers: headers
    })
      .pipe(
        map(this.extractData),
        catchError(this.handleError),
        finalize(() => {
          if (loader) {
            this.loaderService.runningServicesCount--;
            if (this.loaderService.runningServicesCount == 0) {
              this.hideLoader();
            }
          }
        })
      );
  }

  post(url: string, data: any, token?: any, sendAkamaiHeader: boolean = false): Observable<any> {
    return this.posting(true, url, data, sendAkamaiHeader, token);
  }

  postWithoutLoader(url: string, data: any, token?: any, sendAkamaiHeader: boolean = false): Observable<any> {
    return this.posting(false, url, data, sendAkamaiHeader, token);
  }

  private posting(loader: boolean, url: string, data: any, sendAkamaiHeader: boolean, token?: any): Observable<any> {
    if (loader) {
      this.loaderService.runningServicesCount++;
      if (this.loaderService.runningServicesCount >= 1) {
        this.showLoader();
      }
    }

    let headers = new HttpHeaders();
    if (token) {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    } else if (token == "") {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Request-Id': genToken() as string,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    }
    else {
      headers = new HttpHeaders({
        'Authorization': 'Bearer ' + token,
        'SessionId': this.bootstrapParams.sessionGuid
      })
    }

    if (data.hasOwnProperty('confirmationNumbers')) {
      data = data['confirmationNumbers'];
    }

    if (sendAkamaiHeader) {
      headers = this.AddAkamaiBotDetectionHeader(headers);
    }

    return this.http
      .post(url, data, {
        headers: headers,
      })
        .pipe(
          map(this.extractData),
          catchError(this.handleError),
          finalize(() => {
          if (loader) {
            this.loaderService.runningServicesCount--;
            if (this.loaderService.runningServicesCount == 0) {
              this.hideLoader();
            }
          }
          })
        );
  }

  search(url: string, data: any, token?: any, sendAkamaiHeader: boolean = false): Observable<any> {
    this.loaderService.runningServicesCount++;
    if (this.loaderService.runningServicesCount >= 1) {
      this.showLoader();
    }

    let headers = new HttpHeaders();
    if (token) {
      headers = new HttpHeaders({
        'X-HTTP-Method-Override': 'SEARCH',
        'Authorization': 'Bearer ' + token,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    } else if (token == "") {
      headers = new HttpHeaders({
        'X-HTTP-Method-Override': 'SEARCH',
        'X-Request-Id': genToken() as string,
        'SessionId': this.bootstrapParams.sessionGuid
      })
    }
    else {
      headers = new HttpHeaders({
        'X-HTTP-Method-Override': 'SEARCH',
        'SessionId': this.bootstrapParams.sessionGuid
      })
    }

    if (sendAkamaiHeader) {
      headers = this.AddAkamaiBotDetectionHeader(headers);
    }

    return this.http
      .post(url, data, {
        headers: headers,
      })
        .pipe(
          map(this.extractData),
          catchError(this.handleError),
          finalize(() => {
          this.loaderService.runningServicesCount--;

          if (this.loaderService.runningServicesCount == 0) {
            this.hideLoader();
          }
          })
        );
  }

  put(url: string, data: any, token?: any, sendAkamaiHeader: boolean = false): Observable<any> {
    this.loaderService.runningServicesCount++;
    if (this.loaderService.runningServicesCount >= 1) {
      this.showLoader();
    }

    let headers = new HttpHeaders();
    if (token) {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    } else if (token == "") {
      headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Request-Id': genToken() as string,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    }
    else {
      headers = new HttpHeaders({
        'Authorization': 'Bearer ' + token,
        'SessionId': this.bootstrapParams.sessionGuid
      });
    }

    if (sendAkamaiHeader) {
      headers = this.AddAkamaiBotDetectionHeader(headers);
    }

    return this.http
      .put(url, data, {
        headers: headers
      })
        .pipe(
          map(this.extractData),
          catchError(this.handleError),
          finalize(() => {
            this.loaderService.runningServicesCount--;

            if (this.loaderService.runningServicesCount == 0) {
              this.hideLoader();
            }
          })
        );
  }

  delete(url: string, token?: any, data?: any, sendAkamaiHeader: boolean = false): Observable<any> {

    this.loaderService.runningServicesCount++;
    if (this.loaderService.runningServicesCount >= 1) {
      this.showLoader();
    }

    if (!data) {
      let headers = new HttpHeaders();
      if (token) {
        headers = new HttpHeaders({
          'Content-Type': 'text/plain',
          'Authorization': 'Bearer ' + token,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      } else if (token == "") {
        headers = new HttpHeaders({
          'Content-Type': 'text/plain',
          'X-Request-Id': genToken() as string,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }
      else {
        headers = new HttpHeaders({
          'Content-Type': 'text/plain',
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }

      if (sendAkamaiHeader) {
        headers = this.AddAkamaiBotDetectionHeader(headers);
      }

      return this.http.delete(url, {
        headers: headers,
      })
        .pipe(
          map(this.extractData),
          catchError(this.handleError),
          finalize(() => {
            this.loaderService.runningServicesCount--;

            if (this.loaderService.runningServicesCount == 0) {
              this.hideLoader();
            }
          })
        );
    } else {
      let headers = new HttpHeaders();
      if (token) {
        headers = new HttpHeaders({
          'Content-Type': 'text/json',
          'Authorization': 'Bearer ' + token,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      } else if (token == "") {
        headers = new HttpHeaders({
          'Content-Type': 'text/json',
          'X-Request-Id': genToken() as string,
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }
      else {
        headers = new HttpHeaders({
          'Content-Type': 'text/json',
          'SessionId': this.bootstrapParams.sessionGuid
        });
      }

      if (sendAkamaiHeader) {
        headers = this.AddAkamaiBotDetectionHeader(headers);
      }

      // http.delete does not support body, http.request of type delete does
      return this.http.request(
        'delete',
        url, {
        headers: headers,
        body: data
      })
        .pipe(
          map(this.extractData),
          catchError(this.handleError),
          finalize(() => {
            this.loaderService.runningServicesCount--;

            if (this.loaderService.runningServicesCount == 0) {
              this.hideLoader();
            }
          })
        );
    }
  }

  private extractData(res: HttpResponse<any>) {
    let body = res;
    return body || {};
  }

  private handleError(error: any) {
    let errMsg = (error.message) ? error.message :
      error.status ? `${error.status} - ${error.statusText}` : 'ERROR';
    /* tslint:disable:no-console */
    console.error(error, errMsg);
    /* tslint:enable:no-console */
    return throwError(() => error);
  }

  private showLoader(): void {
    this.loaderService.show();
  }

  private hideLoader(): void {
    this.loaderService.hide();
  }

  private AddAkamaiBotDetectionHeader(headers: HttpHeaders): HttpHeaders {
    try {
      // @ts-ignore
      if (bmak) {
        // @ts-ignore
        var telemetryString = bmak.get_telemetry();
        headers = headers.append("Akamai-BM-Telemetry", telemetryString);
      }
    } catch (e) {
      console.log("Could not set Akamai header:" + e);
    }
    return headers;
  }
}
